import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Play, RotateCcw, Award, CheckCircle2, XCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useSfx } from '../../store/sfxStore';
import { useAcademyStore } from '../../store/academyStore';

type BucketType = 'STATE' | 'PROPS' | 'CONST';

interface Item {
    id: number;
    text: string;
    correctBucket: BucketType;
}

const ITEMS: Omit<Item, 'id'>[] = [
    { text: "User's Current Score", correctBucket: "STATE" },
    { text: "App Logo URL", correctBucket: "CONST" },
    { text: "Color Theme from Parent", correctBucket: "PROPS" },
    { text: "Is Modal Open?", correctBucket: "STATE" },
    { text: "List of Shopping Cart Items", correctBucket: "STATE" },
    { text: "API Base URL", correctBucket: "CONST" },
    { text: "Data passed from <App />", correctBucket: "PROPS" },
    { text: "Form Input Text", correctBucket: "STATE" },
    { text: "Button onClick handler from parent", correctBucket: "PROPS" },
    { text: "Math.PI", correctBucket: "CONST" },
];

export const StateSorter = () => {
    const { play } = useSfx();
    const { completeModule } = useAcademyStore();
    
    const [gameState, setGameState] = useState<'idle' | 'playing' | 'gameOver'>('idle');
    const [score, setScore] = useState(0);
    const [lives, setLives] = useState(3);
    const [currentItem, setCurrentItem] = useState<Item | null>(null);
    const [queue, setQueue] = useState<Item[]>([]);
    const [feedback, setFeedback] = useState<'correct' | 'wrong' | null>(null);

    const startGame = () => {
        play('click');
        const shuffled = [...ITEMS].sort(() => Math.random() - 0.5).map((item, i) => ({ ...item, id: i }));
        setQueue(shuffled.slice(1));
        setCurrentItem(shuffled[0]);
        setScore(0);
        setLives(3);
        setFeedback(null);
        setGameState('playing');
    };

    const handleSort = (bucket: BucketType) => {
        if (!currentItem || gameState !== 'playing') return;

        const isCorrect = currentItem.correctBucket === bucket;

        if (isCorrect) {
            play('success');
            setScore(s => s + 100);
            setFeedback('correct');
        } else {
            play('fail');
            setLives(l => l - 1);
            setFeedback('wrong');
        }

        setTimeout(() => {
            setFeedback(null);
            if (lives - (isCorrect ? 0 : 1) <= 0) {
                play('error');
                setGameState('gameOver');
            } else if (queue.length > 0) {
                setCurrentItem(queue[0]);
                setQueue(q => q.slice(1));
            } else {
                play('levelUp');
                setGameState('gameOver');
            }
        }, 500);
    };

    useEffect(() => {
        if (gameState === 'gameOver' && score > 500) {
            // We consider > 500 a win, award XP
            completeModule('state-sorter-game', score);
        }
    }, [gameState]);

    return (
        <div className="min-h-screen bg-slate-900 font-sans text-slate-100 flex flex-col pt-20">
            {/* Header */}
            <div className="bg-slate-800 border-b border-slate-700 p-4">
                <div className="max-w-4xl mx-auto flex items-center justify-between">
                    <Link to="/games" onClick={() => play('click')} className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors font-bold">
                        <ArrowLeft size={20} /> Arcade Menu
                    </Link>
                    <div className="flex items-center gap-6">
                        <div className="text-xl font-bold">
                            <span className="text-slate-400 font-mono">SCORE:</span> <span className="text-green-400 font-mono">{score.toString().padStart(4, '0')}</span>
                        </div>
                        <div className="flex gap-1">
                            {[...Array(3)].map((_, i) => (
                                <div key={i} className={`w-4 h-4 rounded-full ${i < lives ? 'bg-red-500' : 'bg-slate-700'}`} />
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* Game Area */}
            <div className="flex-1 flex flex-col items-center justify-center p-6 max-w-4xl mx-auto w-full relative">
                
                {gameState === 'idle' && (
                    <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="bg-slate-800 p-8 rounded-3xl border border-slate-700 text-center max-w-lg w-full">
                        <div className="w-20 h-20 bg-blue-500 rounded-2xl flex items-center justify-center text-4xl mx-auto mb-6 shadow-lg shadow-blue-500/20">📦</div>
                        <h1 className="text-3xl font-black mb-4 font-comic">State Sorter Engine</h1>
                        <p className="text-slate-400 mb-8 font-medium text-lg leading-relaxed">
                            Sort the incoming concepts into the correct memory bucket before they overflow. Don't mix up Props and State!
                        </p>
                        <button onClick={startGame} className="w-full py-4 bg-green-500 hover:bg-green-400 text-white rounded-xl font-bold flex items-center justify-center gap-2 text-lg shadow-lg shadow-green-500/30 transition-all hover:scale-105">
                            <Play fill="currentColor" /> Start Sorting
                        </button>
                    </motion.div>
                )}

                {gameState === 'playing' && currentItem && (
                    <div className="w-full flex justify-center mt-[-100px]">
                        <AnimatePresence mode="wait">
                            <motion.div
                                key={currentItem.id}
                                initial={{ y: -50, opacity: 0, scale: 0.8 }}
                                animate={{ y: 0, opacity: 1, scale: 1 }}
                                exit={{ y: 50, opacity: 0, scale: 0.8 }}
                                className={`px-8 py-6 rounded-2xl text-center shadow-xl border-4 ${feedback === 'correct' ? 'border-green-500 bg-green-900 border-dashed' : feedback === 'wrong' ? 'border-red-500 bg-red-900 border-dashed' : 'border-slate-600 bg-slate-800'} transition-colors duration-200`}
                            >
                                <div className="text-2xl font-bold max-w-sm font-comic">
                                    {currentItem.text}
                                </div>
                                {feedback === 'correct' && <CheckCircle2 className="mx-auto mt-4 text-green-400" size={32} />}
                                {feedback === 'wrong' && <XCircle className="mx-auto mt-4 text-red-400" size={32} />}
                            </motion.div>
                        </AnimatePresence>
                    </div>
                )}

                {gameState === 'playing' && (
                    <div className="absolute bottom-20 w-full grid grid-cols-3 gap-6">
                        <motion.button 
                            whileHover={{ scale: 1.05, y: -5 }} 
                            whileTap={{ scale: 0.95 }}
                            onClick={() => handleSort('PROPS')}
                            className="bg-purple-900/50 hover:bg-purple-800 border-4 border-purple-500 border-b-8 rounded-3xl p-6 text-center shadow-[0_10px_0_rgba(168,85,247,0.3)] transition-colors group"
                        >
                            <div className="text-4xl mb-4 group-hover:scale-110 transition-transform">🎁</div>
                            <h3 className="font-black text-xl text-purple-300 tracking-wider">PROPS</h3>
                            <p className="text-xs text-purple-200/50 mt-2 font-bold uppercase tracking-widest">Read Only</p>
                        </motion.button>
                        
                        <motion.button 
                            whileHover={{ scale: 1.05, y: -5 }} 
                            whileTap={{ scale: 0.95 }}
                            onClick={() => handleSort('STATE')}
                            className="bg-green-900/50 hover:bg-green-800 border-4 border-green-500 border-b-8 rounded-3xl p-6 text-center shadow-[0_10px_0_rgba(34,197,94,0.3)] transition-colors group"
                        >
                            <div className="text-4xl mb-4 group-hover:scale-110 transition-transform">🧠</div>
                            <h3 className="font-black text-xl text-green-300 tracking-wider">STATE</h3>
                            <p className="text-xs text-green-200/50 mt-2 font-bold uppercase tracking-widest">Mutable</p>
                        </motion.button>

                        <motion.button 
                            whileHover={{ scale: 1.05, y: -5 }} 
                            whileTap={{ scale: 0.95 }}
                            onClick={() => handleSort('CONST')}
                            className="bg-blue-900/50 hover:bg-blue-800 border-4 border-blue-500 border-b-8 rounded-3xl p-6 text-center shadow-[0_10px_0_rgba(59,130,246,0.3)] transition-colors group"
                        >
                            <div className="text-4xl mb-4 group-hover:scale-110 transition-transform">⚓</div>
                            <h3 className="font-black text-xl text-blue-300 tracking-wider">CONST</h3>
                            <p className="text-xs text-blue-200/50 mt-2 font-bold uppercase tracking-widest">Static File</p>
                        </motion.button>
                    </div>
                )}

                {gameState === 'gameOver' && (
                    <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="bg-slate-800 p-8 rounded-3xl border border-slate-700 text-center max-w-md w-full">
                        <h2 className="text-4xl font-black mb-2 font-comic text-white">Shift Over!</h2>
                        <div className="text-6xl font-black text-green-400 my-6 font-mono drop-shadow-[0_0_15px_rgba(74,222,128,0.5)]">
                            {score}
                        </div>
                        {score > 500 ? (
                            <div className="bg-green-500/20 text-green-300 p-4 rounded-xl mb-6 font-bold flex items-center justify-center gap-2">
                                <Award /> You passed the memory test!
                            </div>
                        ) : (
                            <div className="bg-orange-500/20 text-orange-300 p-4 rounded-xl mb-6 font-bold flex items-center justify-center gap-2">
                                Review your memory structures and try again.
                            </div>
                        )}
                        <button onClick={startGame} className="w-full py-4 bg-slate-700 hover:bg-slate-600 text-white rounded-xl font-bold flex items-center justify-center gap-2 text-lg transition-all hover:scale-105 mb-4">
                            <RotateCcw size={20} /> Play Again
                        </button>
                        <Link to="/games" className="w-full py-4 bg-slate-800 border-2 border-slate-700 hover:bg-slate-700 text-slate-300 rounded-xl font-bold flex items-center justify-center gap-2 text-lg transition-all block">
                            Back to Arcade
                        </Link>
                    </motion.div>
                )}

            </div>
        </div>
    );
};
